import React, { useState, useEffect } from 'react';
import { getAllPatients, createPatient, updatePatient, deletePatient } from '../../api';
import styles from './CreateUser.module.css';

const CreateUser = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [formData, setFormData] = useState({
    patientName: '',
    phone: '',
    email: '',
    address: '',
    bloodGroup: ''
  });

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await getAllPatients();
      if (response.success) {
        setPatients(response.data);
      } else {
        showAlert('error', 'Failed to fetch patients');
      }
    } catch (error) {
      showAlert('error', 'Error fetching patients');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      patientName: '',
      phone: '',
      email: '',
      address: '',
      bloodGroup: ''
    });
    setEditingPatient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.patientName || !formData.phone || !formData.address || !formData.bloodGroup) {
      showAlert('error', 'All fields are required');
      return;
    }

    try {
      if (editingPatient) {
        const response = await updatePatient(editingPatient._id, formData);
        if (response.success) {
          showAlert('success', 'Patient updated successfully');
          setShowForm(false);
          resetForm();
          fetchPatients();
        } else {
          showAlert('error', response.message || 'Failed to update patient');
        }
      } else {
        const response = await createPatient(formData);
        if (response.success) {
          showAlert('success', 'Patient created successfully');
          setShowForm(false);
          resetForm();
          fetchPatients();
        } else {
          showAlert('error', response.message || 'Failed to create patient');
        }
      }
    } catch (error) {
      showAlert('error', 'Error saving patient');
    }
  };

  const handleEdit = (patient) => {
    setEditingPatient(patient);
    setFormData({
      patientName: patient.patientName,
      phone: patient.phone,
      email: patient.email || '',
      address: patient.address,
      bloodGroup: patient.bloodGroup
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    const patient = patients.find(p => p._id === id);
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!patientToDelete) return;
    try {
      const response = await deletePatient(patientToDelete._id);
      if (response.success) {
        showAlert('success', 'Patient deleted successfully');
        fetchPatients();
      } else {
        showAlert('error', response.message || 'Failed to delete patient');
      }
    } catch (error) {
      showAlert('error', 'Error deleting patient');
    } finally {
      setShowDeleteModal(false);
      setPatientToDelete(null);
    }
  };

  const handleView = (patient) => {
    setEditingPatient(patient);
    setShowViewModal(true);
  };

  const filteredPatients = patients.filter(patient =>
    patient.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.phone.includes(searchTerm) ||
    patient.bloodGroup.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading patients...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Patient Management</h2>
        <p>Create, edit, delete, and view patient information</p>
      </div>

      {alert.show && (
        <div className={`${styles.alert} ${styles[`alert${alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}`]}`}>
          {alert.message}
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <span>+</span> Add New Patient
        </button>
      </div>

      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search patients by name, phone, or blood group..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {showForm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {editingPatient ? 'Edit Patient' : 'Add New Patient'}
              </h3>
              <button
                className={styles.closeBtn}
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Patient Name *</label>
                <input
                  type="text"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="Enter patient name"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="Enter phone number"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="Enter patient email"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Address *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={styles.textarea}
                  placeholder="Enter address"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Blood Group *</label>
                <select
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  <option value="">Select blood group</option>
                  {bloodGroups.map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnSuccess}`}
                >
                  {editingPatient ? 'Update Patient' : 'Create Patient'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && editingPatient && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Patient Details</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setShowViewModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.viewDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Patient Name</span>
                <span className={styles.detailValue}>{editingPatient.patientName}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Phone</span>
                <span className={styles.detailValue}>{editingPatient.phone}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Email</span>
                <span className={styles.detailValue}>{editingPatient.email || 'N/A'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Address</span>
                <span className={styles.detailValue}>{editingPatient.address}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Blood Group</span>
                <span className={styles.detailValue}>{editingPatient.bloodGroup}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Created</span>
                <span className={styles.detailValue}>{new Date(editingPatient.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tableContainer}>
        {filteredPatients.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No patients found</h3>
            <p>{searchTerm ? 'Try adjusting your search terms' : 'Start by adding a new patient'}</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Patient Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Address</th>
                <th>Blood Group</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {filteredPatients.map(patient => (
                <tr key={patient._id}>
                  <td>{patient.patientName}</td>
                  <td>{patient.phone}</td>
                  <td>{patient.email || 'N/A'}</td>
                  <td>{patient.address}</td>
                  <td>
                    <span style={{
                      backgroundColor: '#e9ecef',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}>
                      {patient.bloodGroup}
                    </span>
                  </td>
                  <td>{new Date(patient.createdAt).toLocaleDateString()}</td>
                  <td className={styles.tableActions}>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
                      onClick={() => handleView(patient)}
                    >
                      View
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}
                      onClick={() => handleEdit(patient)}
                    >
                      Edit
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                      onClick={() => handleDelete(patient._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Confirm Delete</h3>
              <button
                className={styles.closeBtn}
                onClick={() => {
                  setShowDeleteModal(false);
                  setPatientToDelete(null);
                }}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Are you sure you want to delete <b>{patientToDelete?.patientName}</b>?</p>
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => {
                  setShowDeleteModal(false);
                  setPatientToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateUser; 